import { FC, Fragment, useCallback, useMemo, useState } from "react";
import { mergeStyleSets, Pivot as OfficePivot, PivotItem, Stack } from "@fluentui/react";
import { css, ITheme, useTheme } from "cayo.ui";
import styled from "styled-components";
import { Entity, PropertyAnnotation } from "@cayo/shared/api/types/cayo-graph";
import { HttpMethods } from "@cayo/shared/api/types/schema";
import globalConstants from "@cayo/shared/components/App/constants";
import { IPropertiesPanelSignals } from "@cayo/shared/services/propertiesPanelSignal.service";
import { IElementStyles, IPivot, IPivotItem } from "../../../api/schema.api";
import { appSettings } from "@cayo/shared/settings/appSettings";
import MessageCarousel from "../../Alerts/MessageCarousel";
import odataAnnotationsUtils from "../../Form/odata-annotations.utils";
import globalHooks from "../../GlobalHooks";
import ODataScheme from "../../Schemes/ODataScheme";
import { allRendereres } from "../../Schemes/renderers";
import SchemeLoader from "../../Schemes/SchemeLoader";

type PivotProps = Omit<IPivot, "type"> & {
  object: Entity;
  signals?: IPropertiesPanelSignals;
  propertyAnnotations?: PropertyAnnotation[];
  parentContainerName?: string;
  verticalTabs?: boolean; // If true, apply container, verticalFill, horizontal
};

export type TabEx = IPivotItem & {
  isDataset: boolean;
  toRender?: boolean;
};

function getProp(obj: Record<string, any>, field: string, prop: string) {
  return obj[`${field}@cayo.schema.${prop}`];
}

const getClasses = (theme: ITheme, noLinks: boolean, verticalTabs?: boolean) => {
  const boxShadow = (theme.elementStyles as unknown as IElementStyles)?.commandBar["box-shadow"];
  const borderRadius = (theme.elementStyles as unknown as IElementStyles)?.commandBar[
    "border-radius"
  ];

  const result = mergeStyleSets({
    container: {
      display: "flex",
      height: "100%",
      marginBottom: "18px",
      marginRight: "8px",
    },
    links: {
      display: "flex",
      flexDirection: "column",
      width: "200px",
      padding: "0 0 20px 0",

      height: "100%",
      margin: "8px 0px 8px 0px",
      boxShadow,
      borderRadius,
      overflow: "hidden",
      borderRight:
        verticalTabs && !boxShadow && `1px solid ${theme.officeTheme.palette.neutralLight}`,
    },
    link: {
      textAlign: "left",
      marginRight: "0px !important",
      paddingRight: "0px !important",
    },
    content: {
      flex: 1,
      margin: `8px 8xpx 8px ${noLinks ? "8px" : "0px"}`,
      boxShadow,
      borderRadius,
    },
  });
  return result;
};

const isOdataScheme = (item: IPivotItem) => {
  const isFormOrCustomControl =
    item?.schemePath?.toLowerCase()?.indexOf("propertyset") > 0 ||
    item?.schemePath?.toLowerCase()?.indexOf("custom/") === 0;

  return !isFormOrCustomControl;
};

const findSelectedTabIndex = (tabs: TabEx[], selectedItem?: string) => {
  let index = (tabs.length && tabs && tabs.findIndex((i) => i.headerText === selectedItem)) || 0;
  index = Math.max(index, 0);

  return index;
};

const Pivot: FC<PivotProps> = (props) => {
  const { signals } = props;
  const [selectedItem, setSelectedItem] = useState<string | undefined>(
    appSettings.panelSetting.get()?.selectedTabKey
  );
  const theme = useTheme();

  const { userSettings } = globalHooks.useUserSettings();
  const verticalTabs = !!userSettings?.verticalTabs;

  const tabs = useMemo<TabEx[]>(() => {
    let result = props.items
      ?.filter((i) => i.customView?.toLowerCase() !== MessageCarouselView)
      .map<TabEx>((i) => ({ ...i, isDataset: !isOdataScheme(i) }));

    result.push(...odataAnnotationsUtils.toTabs(props.object));

    result = result.sort((a, b) => a.order - b.order);

    const index = result?.length && findSelectedTabIndex(result, selectedItem);
    if (index >= 0) {
      result[index].toRender = true;
      setSelectedItem(result[index].headerText);
    }

    return result;
  }, [props.items]);

  const classes = useMemo(() => getClasses(theme, tabs.length < 2, verticalTabs), [theme, tabs]);

  const auxProps = useMemo(
    () => ({
      object: props?.object,
      signals,
      propertyAnnotations: props?.propertyAnnotations,
    }),
    [props?.object, signals, props?.propertyAnnotations, props?.parentContainerName]
  );

  const handleItemClick = useCallback(
    (pivotItem?: PivotItem): void => {
      const headerText = pivotItem?.props?.headerText;

      const clickedTab = tabs.find((t) => t.headerText === headerText);
      if (clickedTab) {
        clickedTab.toRender = true;
      }

      appSettings.panelSetting.update({ selectedTabKey: headerText });

      setSelectedItem(headerText);
    },
    [tabs]
  );

  const customControlsProps = useMemo(() => {
    const customViewTabs = tabs?.filter((t) => t.customView);
    const result: Record<string, any> = {};

    customViewTabs?.forEach((t) => {
      const renderer = allRendereres[t.customView!.toLowerCase() as keyof typeof allRendereres];
      const prop = t.schemePath.substring(t.schemePath.lastIndexOf("/") + 1);
      const url = "v0/" + t.schemePath.substring(0, t.schemePath.lastIndexOf("/"));

      const isReadOnly = getProp(props.object, prop, "readOnly") || t.readOnly;

      result[t.schemePath] = {
        renderer,
        props: {
          key: props.object.objectPath,
          rootUrl: "v0/" + t.schemePath,
          url,
          method: "PATCH" as HttpMethods,
          parameters: t.parameters,
          isCustomView: true,
          object: props.object[prop],
          parentObjectName: props.object.objectName,
          parentContainerName: t.headerText,
          signals: props.signals,
          readОnly: isReadOnly,
          style: {
            display: selectedItem === t.headerText ? "flex" : "none",
            height: "100%",
          },
        },
      };
    });
    return result;
  }, [tabs, selectedItem, props.object]);

  const generateContent = useCallback(() => {
    return tabs.map((item) => {
      if (item.customView) {
        const tabProps = customControlsProps[item.schemePath];

        if (tabProps?.renderer) {
          const C = tabProps.renderer;
          return <C key={tabProps.props.key} {...tabProps.props} />;
        }
      }

      if (!item.toRender) {
        return <Fragment key={`${props.id}_${item.headerText}_fragment`} />;
      }

      return (
        <Content
          key={`${props.id}_${item.headerText}_pc`}
          isSelected={selectedItem === item.headerText}
        >
          {item.isDataset ? (
            <SchemeLoader
              schemePath={item.schemePath}
              auxProps={{ ...auxProps, parentContainerName: item.headerText }}
            />
          ) : (
            <ODataScheme schemePath={item.schemePath} />
          )}
        </Content>
      );
    });
  }, [props?.object, tabs, selectedItem, customControlsProps]);

  const lastVerificationMessagesUrl = useMemo(() => {
    const lastVerificationTab = props.items?.find(
      (i) => i.customView?.toLowerCase() === MessageCarouselView
    );
    if (lastVerificationTab) {
      const url = globalConstants.apiVersion + lastVerificationTab.schemePath;
      return url;
    }

    return undefined;
  }, []);

  return (
    <Stack
      styles={{ root: { height: "100%" } }}
      {...(verticalTabs
        ? { className: classes.container, verticalFill: true, horizontal: true }
        : {})}
    >
      {lastVerificationMessagesUrl && <MessageCarousel url={lastVerificationMessagesUrl} />}
      {tabs.length > 1 && (
        <OfficePivot
          className={css.px8}
          selectedKey={selectedItem}
          overflowBehavior={!verticalTabs ? "menu" : undefined}
          onLinkClick={handleItemClick}
          headersOnly={true}
          styles={
            verticalTabs
              ? {
                  link: classes.link,
                  root: classes.links,
                  linkContent: { flexDirection: "row" },
                }
              : {}
          }
        >
          {tabs?.map((t) => (
            <PivotItem
              key={`${t!.headerText}`}
              headerText={t.headerText}
              itemKey={`${t!.headerText}`}
            />
          ))}
        </OfficePivot>
      )}
      <ContentWrapper {...(verticalTabs ? { className: classes.content } : {})}>
        {generateContent()}
      </ContentWrapper>
    </Stack>
  );
};

const MessageCarouselView = "messagecarousel";

const ContentWrapper = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-flow: column;
  overflow: hidden;
`;

const Content = styled.div<{ isSelected: boolean }>`
  display: ${(props) => (props.isSelected ? "flex" : "none")};
  height: 100%;
  padding-top: 0px;
  flex-flow: column;
`;

export default Pivot;
